import { z } from 'zod'
import { SeriesAnnualProjection } from './series-report.js'

const Year = z.number().int().min(1000).max(9999)
const Generation = z.number().int().nonnegative()
const Count = z.number().int().nonnegative()
const PublicClass = z
  .object({
    code: z.string(),
    effectiveName: z.string(),
    division: z.object({ code: z.string(), name: z.string() }).strict(),
    count: z.number().int().nonnegative(),
  })
  .strict()
export const SeriesPublicationTrend = z
  .object({
    trendKey: z.string().min(1).max(100),
    label: z.string(),
    class: PublicClass,
  })
  .strict()
export const PublicSeriesRef = z.object({ name: z.string(), slug: z.string() }).strict()
/**
 * Opaque token for the set of active publications a public response was read from. Two
 * responses for one series with the same token describe the same publications.
 */
const PublicationSetRevision = z.string().min(1)
/**
 * How an edition's entrants split against finalized earlier editions of the same series.
 * The three counts sum to the edition's entrant count. Matching is by linked person, so
 * an unmerged duplicate person counts as first-time.
 */
export const SeriesEntrantHistory = z
  .object({
    returning: Count,
    firstTime: Count,
    /** Entrants with no linked person, who cannot be matched to an earlier edition. */
    unlinked: Count,
    /** Finalized earlier editions with at least one counted entrant. */
    earlierEditions: z.number().int().positive(),
  })
  .strict()
const payloadFields = {
  series: PublicSeriesRef,
  year: Year,
  metrics: SeriesAnnualProjection,
  trends: z.array(SeriesPublicationTrend),
}
const SeriesPublicationPayloadV1 = z
  .object({ schemaVersion: z.literal(1), ...payloadFields })
  .strict()
/** What every new publication stores. Null history means no earlier edition to compare. */
export const SeriesPublicationPayloadV2 = z
  .object({
    schemaVersion: z.literal(2),
    ...payloadFields,
    entrantHistory: SeriesEntrantHistory.nullable(),
  })
  .strict()
/** A trend class with its shared model-class key, present only for a validated model link. */
const PublicModelClass = PublicClass.extend({ modelClassKey: z.string().nullable() }).strict()
export const SeriesPublicationPayloadV3 = SeriesPublicationPayloadV2.extend({
  schemaVersion: z.literal(3),
  trends: z.array(SeriesPublicationTrend.extend({ class: PublicModelClass }).strict()),
}).strict()
/**
 * Deliberately excludes the staff report's IDs, source metadata and analytics.
 * Snapshots are immutable, so version 1 and 2 rows stay readable beside version 3.
 */
export const SeriesPublicationPayload = z.discriminatedUnion('schemaVersion', [
  SeriesPublicationPayloadV1,
  SeriesPublicationPayloadV2,
  SeriesPublicationPayloadV3,
])
export type SeriesPublicationPayloadOutput = z.infer<typeof SeriesPublicationPayload>
export const SeriesPublicationPreviewResponse = z
  .object({
    token: z.string(),
    expiresAt: z.iso.datetime(),
    generation: Generation,
    requiresAlignmentAcknowledgment: z.boolean(),
    payload: SeriesPublicationPayloadV3,
  })
  .strict()
export const PublishSeriesEditionRequest = z
  .object({
    token: z.string().min(1).max(100),
    acknowledgeIncompleteAlignment: z.boolean().default(false),
  })
  .strict()
export const UnpublishSeriesEditionRequest = z.object({ expectedGeneration: Generation }).strict()
export const SeriesPublicationReceipt = z
  .object({
    publicationId: z.guid().nullable(),
    generation: Generation,
    publishedAt: z.iso.datetime().nullable(),
  })
  .strict()
export const SeriesPublicationAuditResponse = z
  .object({
    publicationId: z.guid(),
    payload: SeriesPublicationPayload,
    publishedAt: z.iso.datetime(),
  })
  .strict()
export const PublicSeriesReportQuery = z
  .object({
    fromYear: z.coerce.number().int().min(1000).max(9999),
    toYear: z.coerce.number().int().min(1000).max(9999),
    trendKeys: z
      .string()
      .max(10099)
      .transform(value => (value === '' ? [] : value.split(',')))
      .pipe(
        z
          .array(z.string().min(1).max(100))
          .max(100)
          .refine(keys => new Set(keys).size === keys.length, 'Choose distinct trend keys.')
      )
      .optional(),
  })
  .strict()
  .refine(
    q => q.toYear >= q.fromYear && q.toYear - q.fromYear < 50,
    'Choose an inclusive range of at most 50 years.'
  )
export const PublicSeriesReportResponse = z
  .object({
    series: PublicSeriesRef,
    revision: PublicationSetRevision,
    fromYear: Year,
    toYear: Year,
    years: z
      .array(
        z
          .object({
            year: Year,
            edition: z
              .object({
                metrics: SeriesAnnualProjection,
                /** Null for a first edition and for a snapshot published before the split existed. */
                entrantHistory: SeriesEntrantHistory.nullable(),
                publishedAt: z.iso.datetime(),
              })
              .strict()
              .nullable(),
          })
          .strict()
      )
      .max(50),
    trends: z
      .array(
        z
          .object({
            trendKey: z.string(),
            label: z.string(),
            cells: z
              .array(
                z
                  .object({
                    year: Year,
                    label: z.string().nullable(),
                    class: PublicModelClass.nullable(),
                  })
                  .strict()
              )
              .max(50),
          })
          .strict()
      )
      .max(100),
  })
  .strict()
export type PublicSeriesReportOutput = z.infer<typeof PublicSeriesReportResponse>

export const SeriesPublicationStatus = z
  .object({
    generation: Generation,
    activePublication: z
      .object({ publicationId: z.guid(), publishedAt: z.iso.datetime() })
      .strict()
      .nullable(),
    finalized: z.boolean(),
    canManage: z.boolean(),
    publicSlug: z.string(),
  })
  .strict()
export const PublicSeriesTrendsQuery = z
  .object({
    fromYear: z.coerce.number().int().min(1000).max(9999).optional(),
    toYear: z.coerce.number().int().min(1000).max(9999).optional(),
    cursor: z.string().min(1).max(100).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
  })
  .strict()
  .refine(
    q =>
      (q.fromYear === undefined && q.toYear === undefined) ||
      (q.fromYear !== undefined &&
        q.toYear !== undefined &&
        q.toYear >= q.fromYear &&
        q.toYear - q.fromYear < 50),
    'Choose both years spanning at most 50 years.'
  )
export const PublicSeriesTrendsResponse = z
  .object({
    series: PublicSeriesRef,
    revision: PublicationSetRevision,
    fromYear: Year,
    toYear: Year,
    trends: z.array(z.object({ trendKey: z.string(), label: z.string() }).strict()).max(100),
    nextCursor: z.string().nullable(),
  })
  .strict()
export type SeriesPublicationStatusOutput = z.infer<typeof SeriesPublicationStatus>
export type SeriesPublicationPreviewOutput = z.infer<typeof SeriesPublicationPreviewResponse>
export type SeriesPublicationReceiptOutput = z.infer<typeof SeriesPublicationReceipt>
export type PublicSeriesTrendsOutput = z.infer<typeof PublicSeriesTrendsResponse>
